import { z } from 'zod';

/**
 * Speed tests — two layers, one record.
 *
 * ## The line test
 *
 * The subscriber's CPE measures its own line, over TR-143 (the standard
 * `DownloadDiagnostics` / `UploadDiagnostics` / `IPPingDiagnostics` objects
 * every CPE class on the fleet exposes — F670L, HG8546M, HG8145V6 alike). The
 * platform arms the download against a public URL on itself, waits for the
 * CPE to report, reads the leaves back, chains the upload the same way and
 * then a ping to the platform host. The three numbers that come out are what
 * the CPE saw from ITS side of the fibre: an honest answer to "is it the line
 * or the customer's laptop", which no test run from a laptop can give.
 *
 * Which PATH was measured is part of the answer, not a footnote. A routed
 * subscriber's default route is their WAN, so the download rides the internet
 * path the customer pays for. A bridged unit has only its management address:
 * the same test measures the management tunnel, and the record says so
 * (`path`) rather than letting a tunnel figure pass for a WAN one. Since
 * 2026-09-13 the operator may also CHOOSE (`startLineTestSchema.path`, from
 * the plan's `pathOptions`): a routed unit is offered both and defaults to
 * the WAN, a bridged one is offered the tunnel alone, one the platform
 * cannot judge starts on NO choice (`defaultPath: null` — an untouched Run
 * keeps `unknown`), and a tunnel run is pinned to the management
 * connection through the TR-143 `Interface` leaf so a bridged unit's run
 * actually completes.
 *
 * ## The phone test
 *
 * The technician's handset measures where THEY are standing — over the
 * customer's WiFi if they are on it — against the same endpoints, and posts
 * the numbers here so the two layers sit in one history against the ONU. A
 * phone result carries `path: phone` and its raw samples in `detail`, the way
 * a line result carries the raw TR-143 leaves.
 *
 * ## Units
 *
 * `downBps` / `upBps` are BITS per second — the number on the subscriber's
 * plan — computed as `TestBytesReceived * 8 / (EOMTime - BOMTime)` per
 * TR-143. `bytesDown` / `bytesUp` are the byte counts the rate was computed
 * from, kept so a suspicious figure can be re-derived. Latency and jitter are
 * milliseconds; `lossPct` is 0..100.
 */

export const SPEED_TEST_KINDS = ['line', 'phone'] as const;
export const speedTestKindSchema = z.enum(SPEED_TEST_KINDS);
export type SpeedTestKind = z.infer<typeof speedTestKindSchema>;

/**
 * `queued`    — the CPE has been asked (or the phone token minted) and nothing
 *               has come back yet.
 * `running`   — at least one phase has reported; the run is mid-chain.
 * `done`      — every phase reported, the numbers are final.
 * `failed`    — the CPE answered an `Error_*` state or a CWMP fault; `error`
 *               carries the CPE's own word, never a paraphrase.
 * `timed_out` — the CPE never reported inside the wait; `error` says how long
 *               was waited and which phase was outstanding.
 */
export const SPEED_TEST_STATUSES = ['queued', 'running', 'done', 'failed', 'timed_out'] as const;
export const speedTestStatusSchema = z.enum(SPEED_TEST_STATUSES);
export type SpeedTestStatus = z.infer<typeof speedTestStatusSchema>;

/**
 * What the measured bytes actually travelled over.
 *
 * `subscriber_wan`    — the CPE's default route is the subscriber WAN: the
 *                       internet path the customer pays for.
 * `management_tunnel` — a bridged unit's only address is its management one,
 *                       so the test rode the management VLAN and tunnel.
 * `phone`             — the handset's own connection, wherever it was.
 * `unknown`           — the platform could not tell: the ONU has no internet
 *                       VLAN on record, is not a hotspot unit by name, and its
 *                       CPE document names no WAN connection to judge by. The
 *                       figure is real; what it travelled over is NOT claimed,
 *                       because a tunnel figure shown as a WAN one is the one
 *                       lie this feature must never tell.
 */
export const SPEED_TEST_PATHS = ['subscriber_wan', 'management_tunnel', 'phone', 'unknown'] as const;
export const speedTestPathSchema = z.enum(SPEED_TEST_PATHS);
export type SpeedTestPath = z.infer<typeof speedTestPathSchema>;

export const SPEED_TEST_PATH_LABELS: Record<SpeedTestPath, string> = {
  subscriber_wan: 'subscriber WAN',
  management_tunnel: 'management tunnel',
  phone: 'this phone',
  unknown: 'path not known',
};

/**
 * The sentence a result's path earns, for a card or a status line: which path
 * the ONU measured, or — for `unknown` — the honest admission, so no client
 * has to invent its own wording for the case the record cannot settle.
 */
export function describeSpeedTestPath(path: SpeedTestPath): string {
  switch (path) {
    case 'subscriber_wan':
      return 'measured over the subscriber WAN';
    case 'management_tunnel':
      return 'measured over the management tunnel';
    case 'phone':
      return 'measured by a phone';
    case 'unknown':
      return 'path not known — the ONU has no internet VLAN on record and its CPE names no WAN';
  }
}

/** Which phase of a line test the CPE is on, for the gauge's phase label. */
export const SPEED_TEST_PHASES = ['download', 'upload', 'ping'] as const;
export const speedTestPhaseSchema = z.enum(SPEED_TEST_PHASES);
export type SpeedTestPhase = z.infer<typeof speedTestPhaseSchema>;

/** One test, as every client reads it. */
export const speedTestSchema = z.object({
  id: z.string(),
  onuId: z.string().nullable(),
  deviceId: z.string().nullable(),
  kind: speedTestKindSchema,
  status: speedTestStatusSchema,
  path: speedTestPathSchema,
  /** The phase the CPE is on while `queued` / `running`; null once settled. */
  phase: speedTestPhaseSchema.nullable(),
  requestedBy: z.string().nullable(),
  requestedAt: z.number(),
  startedAt: z.number().nullable(),
  finishedAt: z.number().nullable(),
  /** Bits per second. */
  downBps: z.number().nullable(),
  upBps: z.number().nullable(),
  latencyMs: z.number().nullable(),
  jitterMs: z.number().nullable(),
  lossPct: z.number().nullable(),
  bytesDown: z.number().nullable(),
  bytesUp: z.number().nullable(),
  /**
   * The raw material: the TR-143 leaves a line test read back (per phase),
   * or the phone's samples. Kept verbatim so a number can be re-derived.
   */
  detail: z.record(z.unknown()).nullable(),
  /** The CPE's own state word, or the platform's sentence, when it did not finish. */
  error: z.string().nullable(),
});
export type SpeedTest = z.infer<typeof speedTestSchema>;

/** `GET /api/onus/:id/speedtests` */
export const speedTestHistorySchema = z.object({
  items: z.array(speedTestSchema),
});
export type SpeedTestHistory = z.infer<typeof speedTestHistorySchema>;

export const speedTestHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

/**
 * The two paths an operator may ASK a line test to measure over — never
 * `unknown` (nobody asks for that) and never `phone` (a handset's, not the
 * CPE's). The platform pins the transfer to one of them through the TR-143
 * `Interface` leaf: the management connection's path for the tunnel, the
 * factory empty string (the default route) for the WAN — live 2026-09-13 on
 * an F670L V9 and an HG8546M, both fetching the payload over the tunnel from
 * their management addresses once the leaf named that connection.
 */
export const LINE_TEST_PATH_CHOICES = ['subscriber_wan', 'management_tunnel'] as const;
export const lineTestPathChoiceSchema = z.enum(LINE_TEST_PATH_CHOICES);
export type LineTestPathChoice = z.infer<typeof lineTestPathChoiceSchema>;

/**
 * `POST /api/onus/:id/speedtest` — start a line test. `dryRun` answers the
 * plan (which device, which URL, which path) without arming the CPE; the MCP
 * tool reads that before it is allowed to run live. `path` is the operator's
 * choice from the plan's `pathOptions` (the card's WAN / Management chooser);
 * absent, the platform decides as it always has — the VLAN record, the
 * hotspot name, the CPE's own connections.
 */
export const startLineTestSchema = z.object({
  dryRun: z.boolean().optional(),
  path: lineTestPathChoiceSchema.optional(),
});
export type StartLineTestRequest = z.infer<typeof startLineTestSchema>;

export const lineTestPlanSchema = z.object({
  onuId: z.string(),
  serial: z.string(),
  deviceId: z.string(),
  /** The path the run WOULD measure over: the operator's choice when the request carried one, else the platform's verdict. */
  path: speedTestPathSchema,
  /**
   * What this ONU can be measured over, for a chooser: the WAN when the
   * unit is routed (recorded, or a CPE document showing a routed subscriber
   * connection holding an address) or the platform cannot tell (the
   * operator at the unit may claim it), the management tunnel alone when
   * the unit is a hotspot, recorded bridged, or its CPE's subscriber side
   * is bridged or absent — and the tunnel is on offer beside the WAN ONLY
   * when the CPE document names a management connection to pin the run to
   * (review, 2026-09-13: the chooser offered a tunnel run the API then
   * refused). Never empty: a bridged unit keeps its one option even when
   * the run would be refused, so the card can still draw the chooser.
   */
  pathOptions: z.array(lineTestPathChoiceSchema),
  /**
   * Where the chooser starts: `subscriber_wan` for a routed unit,
   * `management_tunnel` for a bridged one — and NULL when the platform has
   * no verdict (`path` is `unknown`: no VLAN record, and a document with no
   * connections or a routed subscriber connection holding no lease). The
   * chooser then starts on no choice, and a Run with nothing picked keeps
   * the platform's `unknown` rather than being dressed up as a WAN run
   * (review, 2026-09-13: the card posted the WAN default on every
   * undecidable unit, and an ~11 Mbit/s tunnel figure was filed as the
   * subscriber's, marked as the operator's choice).
   */
  defaultPath: lineTestPathChoiceSchema.nullable(),
  /**
   * The download URL the CPE is pointed at, with the run's token written as
   * `[token]`: the URL is the run's credential and a plan is read by people
   * and by an MCP transcript, neither of which may hold a live token. The
   * CPE alone receives the real one, inside the setParameterValues.
   */
  downloadUrl: z.string(),
  uploadUrl: z.string(),
  /** The host the ping phase targets. */
  pingHost: z.string(),
  /** Bytes the download serves and the upload expects. */
  downloadBytes: z.number(),
  uploadBytes: z.number(),
});
export type LineTestPlan = z.infer<typeof lineTestPlanSchema>;

export const startLineTestResultSchema = z.object({
  dryRun: z.boolean(),
  plan: lineTestPlanSchema,
  /** Null on a dry run. */
  run: speedTestSchema.nullable(),
});
export type StartLineTestResult = z.infer<typeof startLineTestResultSchema>;

// ---------------------------------------------------------------------------
// The phone test
// ---------------------------------------------------------------------------

/** `POST /api/speedtest/phone` — mint a run and its token for the handset. */
export const mintPhoneTestSchema = z.object({
  /** The ONU the technician is standing at, so the result files against it. */
  onuId: z.string().min(1).optional(),
});
export type MintPhoneTestRequest = z.infer<typeof mintPhoneTestSchema>;

export const phoneTestTicketSchema = z.object({
  runId: z.string(),
  /** The per-run token the three public endpoints take in their path. */
  token: z.string(),
  /** Absolute URLs, so the app does not have to know where the platform is. */
  downloadUrl: z.string(),
  uploadUrl: z.string(),
  pingUrl: z.string(),
  /** How long the token is good for, epoch ms. */
  expiresAt: z.number(),
  /** The byte budget the token carries across every request it makes. */
  byteBudget: z.number(),
  /** What the download endpoint streams per request. */
  downloadBytes: z.number(),
});
export type PhoneTestTicket = z.infer<typeof phoneTestTicketSchema>;

/** One throughput sample the handset took mid-run: elapsed ms → cumulative bytes. */
export const phoneSampleSchema = z.object({
  t: z.number().min(0),
  bytes: z.number().min(0),
});

/**
 * The ceilings a phone's numbers are held under. The rates and byte counts
 * land in `bigint` columns, and a JSON number is a double: anything past 2^53
 * is already imprecise and anything past 2^63 Postgres refuses outright, so a
 * body with `1e300` in it would pass an unbounded schema and turn into a 500
 * with the run left open. A terabit per second and a terabyte moved are far
 * past any handset and comfortably inside both limits; a fortnight of latency
 * likewise.
 */
export const PHONE_RESULT_MAX_BPS = 1e12;
export const PHONE_RESULT_MAX_BYTES = 1e12;
export const PHONE_RESULT_MAX_MS = 1e9;

/** `POST /api/speedtest/phone/:runId/result` — what the handset measured. */
export const phoneTestResultSchema = z.object({
  downBps: z.number().min(0).max(PHONE_RESULT_MAX_BPS).nullable(),
  upBps: z.number().min(0).max(PHONE_RESULT_MAX_BPS).nullable(),
  latencyMs: z.number().min(0).max(PHONE_RESULT_MAX_MS).nullable(),
  jitterMs: z.number().min(0).max(PHONE_RESULT_MAX_MS).nullable(),
  lossPct: z.number().min(0).max(100).nullable(),
  bytesDown: z.number().int().min(0).max(PHONE_RESULT_MAX_BYTES).nullable(),
  bytesUp: z.number().int().min(0).max(PHONE_RESULT_MAX_BYTES).nullable(),
  /** Raw samples, capped so a phone cannot post a megabyte of "detail". */
  samples: z
    .object({
      down: z.array(phoneSampleSchema).max(400).optional(),
      up: z.array(phoneSampleSchema).max(400).optional(),
      pings: z.array(z.number().min(0).nullable()).max(50).optional(),
    })
    .optional(),
  /** What the phone was on when it measured, in its own words ("WiFi", "cellular"). */
  network: z.string().max(80).optional(),
  /** Why the run did not finish, when it did not. */
  error: z.string().max(500).optional(),
});
export type PhoneTestResult = z.infer<typeof phoneTestResultSchema>;

// ---------------------------------------------------------------------------
// Pure helpers both ends read
// ---------------------------------------------------------------------------

/**
 * A bit rate as the number on a plan: bits, stepping 1000 to the unit. A gap
 * — nothing was measured — is a dash, not a zero, because a zero is a reading.
 */
export function formatBitRate(bps: number | null | undefined): string {
  if (bps === null || bps === undefined || !Number.isFinite(bps) || bps < 0) return '—';
  const units = ['bit/s', 'kbit/s', 'Mbit/s', 'Gbit/s'] as const;
  let value = bps;
  let i = 0;
  while (value >= 1000 && i < units.length - 1) {
    value /= 1000;
    i += 1;
  }
  const digits = i === 0 ? 0 : value < 10 ? 2 : value < 100 ? 1 : 0;
  return `${value.toFixed(digits)} ${units[i]}`;
}

/**
 * The same rate split into the number and the unit, for a gauge that draws
 * the two at different sizes. Megabits are the fleet's unit — a plan is sold
 * in them — so anything under a gigabit reads in Mbit/s, one decimal, and
 * only a rate under a megabit drops to kbit/s.
 */
export function splitBitRate(bps: number | null | undefined): { value: string; unit: string } {
  if (bps === null || bps === undefined || !Number.isFinite(bps) || bps < 0) {
    return { value: '—', unit: 'Mbit/s' };
  }
  if (bps >= 1_000_000_000) return { value: (bps / 1e9).toFixed(2), unit: 'Gbit/s' };
  if (bps >= 1_000_000) {
    const m = bps / 1e6;
    return { value: m >= 100 ? m.toFixed(0) : m.toFixed(1), unit: 'Mbit/s' };
  }
  return { value: (bps / 1e3).toFixed(0), unit: 'kbit/s' };
}

// --- The path chooser, shared by the web card, the phone and the MCP tool ------------

/**
 * The line test's path chooser (operator, 2026-09-13: "add a drop down to
 * choose wan or mngt to test on, default is wan for routed devices, mngt only
 * for bridged devices"). What it offers and where it starts come from the
 * PLAN (a dry run of `POST /onus/:id/speedtest`: `pathOptions` +
 * `defaultPath`), never from a client's own reading of the ONU: the
 * platform's verdict on what a unit can measure is the one the run is
 * validated against, so no client can offer a run the API would refuse.
 * Moved here from the web app on 2026-09-15 so the phone's chooser and the
 * MCP tool's `path` argument read the same rule (the named gap: "MCP and
 * mobile line tests send no path").
 *
 * Two questions, answered apart (review, 2026-09-13): what the chooser SHOWS
 * (`effectiveLineTestPath`: the pick, else the plan's default, else the "Not
 * known" placeholder) and what Run POSTS (`requestedLineTestPath`: the
 * operator's ACTIVE pick and nothing else — an untouched chooser posts `{}`
 * and the platform's own verdict stands, so `pathSource: operator` is true
 * only when it was).
 */
export const LINE_TEST_PATH_CHOICE_LABELS: Record<LineTestPathChoice, string> = {
  subscriber_wan: 'WAN',
  management_tunnel: 'Management',
};

/** The placeholder an undecidable unit's chooser starts on — "post no path; the platform says what it can". */
export const LINE_TEST_PATH_UNDECIDED_LABEL = 'Not known';

/** One option of the chooser, in the plan's order; `""` is the undecided placeholder. */
export interface LineTestPathChoiceOption {
  value: LineTestPathChoice | '';
  label: string;
}

/** The options shown — none until the plan has answered; "Not known" first when the plan names no default. */
export function lineTestPathChoices(
  plan: Pick<LineTestPlan, 'pathOptions' | 'defaultPath'> | null | undefined,
): LineTestPathChoiceOption[] {
  if (!plan) return [];
  const offered: LineTestPathChoiceOption[] = plan.pathOptions.map((value) => ({
    value,
    label: LINE_TEST_PATH_CHOICE_LABELS[value],
  }));
  return plan.defaultPath === null ? [{ value: '', label: LINE_TEST_PATH_UNDECIDED_LABEL }, ...offered] : offered;
}

/** The value the chooser SHOWS: the pick while the plan still offers it, else the plan's default, else null. */
export function effectiveLineTestPath(
  plan: Pick<LineTestPlan, 'pathOptions' | 'defaultPath'> | null | undefined,
  picked: LineTestPathChoice | null,
): LineTestPathChoice | null {
  if (!plan) return null;
  if (picked && plan.pathOptions.includes(picked)) return picked;
  return plan.defaultPath;
}

/** The path Run POSTS: the ACTIVE pick while the plan still offers it; null = post `{}`, the platform decides. */
export function requestedLineTestPath(
  plan: Pick<LineTestPlan, 'pathOptions'> | null | undefined,
  picked: LineTestPathChoice | null,
): LineTestPathChoice | null {
  if (!plan || !picked) return null;
  return plan.pathOptions.includes(picked) ? picked : null;
}

/** The body Run posts — `{ path }` for an active pick the plan offers, `{}` otherwise. */
export function lineTestPathBody(
  plan: Pick<LineTestPlan, 'pathOptions'> | null | undefined,
  picked: LineTestPathChoice | null,
): { path?: LineTestPathChoice } {
  const path = requestedLineTestPath(plan, picked);
  return path ? { path } : {};
}

/** The chooser's tooltip: what the two words mean, and WHY there is only one when there is. */
export function lineTestPathChooserTitle(plan: Pick<LineTestPlan, 'pathOptions' | 'defaultPath'>): string {
  const both = 'WAN: the subscriber’s internet path. Management: the management tunnel to this platform.';
  if (plan.defaultPath === null) {
    return `The platform cannot tell what this unit measures over — run as is and the result says so, or pick a path to claim it. ${
      plan.pathOptions.length > 1 ? both : 'WAN: the subscriber’s internet path.'
    }`;
  }
  if (plan.pathOptions.length > 1) return both;
  return plan.pathOptions[0] === 'management_tunnel'
    ? 'This unit is bridged: it has only its management address, so the tunnel is what it can measure.'
    : 'This CPE names no management connection to pin a tunnel run to, so the subscriber WAN is what it can measure.';
}
